/**
 * 购买弹窗（参照竞品：周期"选择诱惑"+ 席位数量 + 订单明细 + 扫码支付占位）。
 * 个人版无席位；团队版席位步进（基础 seatsMin，每加 1 席位按单价累加）。
 * 支付/开通为占位（真实支付对接待接入后端）。
 */
import { useState } from 'react';
import { Minus, Plus } from 'lucide-react';

import { PERIODS, bonusPct, formatYuan, priceCents, type Package, type Period } from '../../model/packages';
import { Modal } from '../../ui/Modal';

function periodLabel(p: Period): string {
  return p === 'year' ? '1 年' : p === 'quarter' ? '3 个月' : '1 个月';
}
function validUntil(p: Period): string {
  const d = new Date();
  if (p === 'year') d.setFullYear(d.getFullYear() + 1);
  else if (p === 'quarter') d.setMonth(d.getMonth() + 3);
  else d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
}

export function PurchaseModal({ pkg, initialPeriod, onClose }: { pkg: Package; initialPeriod: Period; onClose: () => void }) {
  const [period, setPeriod] = useState<Period>(initialPeriod);
  const [seats, setSeats] = useState<number>(pkg.seatsMin || 1);
  const unit = priceCents(pkg, period);
  const total = pkg.perSeat ? unit * seats : unit;
  const bonus = bonusPct(pkg, period);

  const periodBtn = (k: Period): string =>
    `flex-1 rounded-control border px-3 py-2 text-left transition-colors ${period === k ? 'border-accent bg-accent/10' : 'border-surface-line hover:border-accent/50'}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={`开通「${pkg.name}」`}
      summary="选择周期与席位，扫码支付（支付对接待接入后端）"
      footer={
        <div className="flex w-full items-center justify-between">
          <span className="text-ui text-ink-muted">应付金额</span>
          <span className="text-xl font-bold text-accent">{formatYuan(total)}</span>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* 周期选择（含"更省"诱导） */}
        <div>
          <div className="mb-1.5 text-uiSm text-ink-faint">选择周期</div>
          <div className="flex gap-2">
            {PERIODS.map((p) => (
              <button key={p.k} type="button" onClick={() => setPeriod(p.k)} className={periodBtn(p.k)}>
                <div className="text-uiSm text-ink-secondary">{periodLabel(p.k)}</div>
                <div className="text-ui font-semibold text-ink-primary">{formatYuan(priceCents(pkg, p.k))}{pkg.perSeat ? '/席位' : ''}</div>
                {p.k !== 'month' && <div className="mt-0.5 text-[10px] text-accent">{p.tag}</div>}
              </button>
            ))}
          </div>
        </div>

        {/* 席位数量（团队版） */}
        {pkg.perSeat && (
          <div className="flex items-center justify-between">
            <span className="text-uiSm text-ink-faint">席位数量（{pkg.seatsMin}–{pkg.seatsMax}）</span>
            <div className="inline-flex items-center gap-2 rounded-control border border-surface-line bg-surface-base px-2 py-1">
              <button type="button" aria-label="减" onClick={() => setSeats((s) => Math.max(pkg.seatsMin, s - 1))} className="text-ink-secondary hover:text-ink-primary"><Minus size={14} /></button>
              <span className="w-8 text-center font-num text-ui text-ink-primary">{seats}</span>
              <button type="button" aria-label="加" onClick={() => setSeats((s) => Math.min(pkg.seatsMax, s + 1))} className="text-ink-secondary hover:text-ink-primary"><Plus size={14} /></button>
            </div>
          </div>
        )}

        {/* 订单明细 */}
        <div className="flex flex-col gap-1.5 rounded-control border border-surface-line bg-surface-base/50 p-3 text-uiSm">
          <div className="flex justify-between"><span className="text-ink-muted">有效期</span><span className="text-ink-primary">至 {validUntil(period)}</span></div>
          {pkg.perSeat && <div className="flex justify-between"><span className="text-ink-muted">席位</span><span className="text-ink-primary">{seats} 席位</span></div>}
          {bonus > 0 && <div className="flex justify-between"><span className="text-ink-muted">额度加送</span><span className="text-accent">+{bonus}%</span></div>}
          <div className="mt-1 flex justify-between border-t border-surface-line pt-1.5"><span className="text-ink-muted">商品原价</span><span className="text-ink-primary">{formatYuan(total)}</span></div>
        </div>

        {/* 支付二维码占位 */}
        <div className="flex flex-col items-center gap-2 rounded-control border border-dashed border-surface-line p-4">
          <div className="flex h-28 w-28 items-center justify-center rounded bg-surface-base text-uiSm text-ink-faint">支付二维码</div>
          <span className="text-uiSm text-ink-faint">支付宝 / 微信扫码支付（接入后端后生效）</span>
        </div>
      </div>
    </Modal>
  );
}
